import { Util, devLog } from "Util";
import { TotalMapType } from "apis/library/library.api";
import { DEV_MODE, FetchManager, HOST } from "config";
import { UserManager } from "services/util/UserManager";
import {
  UserCreateFormType,
  UserListType,
  UserSearchFormType,
  UserGetFormType,
  UserUpdateFormType,
} from "types/admin/AdminType";
import { create } from "zustand";

export interface UserStoreType {
  userList: UserListType[];
  userIdList: UserListType[];
  userTotalMap: TotalMapType;
  getUserList: () => Promise<boolean>;
  getUserIdList: () => Promise<boolean>;
  getLoginIdCheckDuplicate: (loginId: string) => Promise<boolean>;
  getUser: (userId: string) => Promise<UserGetFormType>;
  createUser: (form: UserCreateFormType) => Promise<any>;
  updateUser: (form: UserUpdateFormType, userId: string) => Promise<any>;
  deleteUser: (userId: string) => Promise<boolean>;
  fetchUserList: () => Promise<any>;
  fetchMyUserList: () => Promise<{ userId: string; loginId: string }[]>;
  fetchMyGroupList: () => Promise<{ groupId: string; groupName: string }[]>;
  resetUserList: () => void;
}

const useUserAPIStore = create<UserStoreType>((set) => ({
  userList: [],
  userIdList: [],

  userTotalMap: { totalRows: 0, totalPages: 0, allRows: 0 },

  async getUserList() {
    try {
      const url = `${HOST}/user`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        set({ userList: data.resultList });
        set({ userTotalMap: data.totalMap });
        return true;
      } else throw new Error();
    } catch (e) {
      devLog("[getUserList] 오류 발생", e);
      return false;
    }
  },

  async getUserIdList() {
    try {
      const url = `${HOST}/user/ids`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        set({ userIdList: data.resultList });

        return true;
      } else throw new Error();
    } catch (e) {
      devLog("[getUserIdList] 오류 발생", e);
      return false;
    }
  },

  async getLoginIdCheckDuplicate(loginId: string) {
    try {
      const url = `${HOST}/user/check-duplicate/${loginId}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        return data.resultObject;
      } else throw new Error();
    } catch (e) {
      devLog("[getUserList] 오류 발생", e);
      return false;
    }
  },

  async createUser(form: UserCreateFormType) {
    try {
      const url = `${HOST}/user`;
      const res = await FetchManager.post(url, {
        ...form,
        username: Util.normalizeSearch(form.username),
      });
      const data = await Util.fetchAndSetData(res);

      if (data) {
        return data;
      } else throw new Error(data.message);
    } catch (e) {
      devLog("[createUser] 오류 발생", e);
    }
  },
  async deleteUser(userId: UserListType["userId"]) {
    try {
      const url = `${HOST}/user/${userId}`;
      const res = await FetchManager.delete(url);
      const data = await Util.fetchAndSetData(res);

      if (data?.isSuccess) {
        return true;
      } else throw new Error();
    } catch (e) {
      devLog("[createUser] 오류 발생", e);
      return false;
    }
  },

  async updateUser(form: UserUpdateFormType, userId: string) {
    try {
      const url = `${HOST}/user/${userId}`;
      const res = await FetchManager.patch(url, {
        ...form,
        username: Util.normalizeSearch(form.username as string),
      });
      const data = await Util.fetchAndSetData(res);

      if (data) {
        return data;
      } else throw new Error();
    } catch (e) {
      devLog("[updateUser] 오류 발생", e);
      return false;
    }
  },

  async getUser(userId: UserGetFormType["userId"]) {
    try {
      const url = `${HOST}/user/${userId}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data?.isSuccess) {
        return data.resultObject;
      } else throw new Error();
    } catch (e) {
      devLog("[getUser] 오류 발생", e);
      return false;
    }
  },

  async fetchUserList() {
    try {
      const url = `${HOST}/user`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);
      if (data) {
        return data.resultList;
      } else {
        throw new Error();
      }
    } catch (e) {
      devLog("[fetchUserListData] 오류 발생", e);
      return false;
    }
  },

  async fetchMyUserList() {
    try {
      const url = `${HOST}/user/groups/users`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);
      if (data) {
        return data.resultList;
      } else {
        throw new Error();
      }
    } catch (e) {
      devLog("[fetchUserListData] 오류 발생", e);
      return false;
    }
  },

  async fetchMyGroupList() {
    try {
      const url = `${HOST}/groups/my-groups`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);
      if (data) {
        return data.resultList;
      } else {
        throw new Error();
      }
    } catch (e) {
      devLog("[fetchUserListData] 오류 발생", e);
      return false;
    }
  },

  resetUserList: () => {
    set({
      userList: [],
      userTotalMap: { totalRows: 0, totalPages: 0, allRows: 0 },
    });
  },
}));

export default useUserAPIStore;
